<script lang="ts">
    import { onDestroy, onMount } from 'svelte';
    import { get } from 'svelte/store';

    import closePhoneIcon from '../assets/close_phone.png';
    import countryIcon from '../assets/country_icon.png';
    import { CREDIT_CARD_PROTOCOL } from '../constants/common';
    import { COOKIE_SCROLL_DURATION_MS } from '../constants/cookieLayout';
    import { themeType as ThemeTypeEnum } from '../constants/Enums';
    import { POPUP_ID, JUMP_TYPE_ENUM } from '../enums';
    import {
        AWARD_PAGE_EXPOSE_SW,
        REG_PAGE_STAY_DUR_SW,
        PAGE_CLICK_CK,
        PHONE_VERIFY_SW,
        REG_LOGIN_FAIL_SW,
        REG_LOGIN_SUCCESS_SW,
        REG_PHONE_INPUT_SW
    } from '../enums/track';
    import { receiveReward } from '../model/receive';
    import { isLogin, authChecking } from '../store';
    import {
        activityId,
        isJumpOneLInk,
        cookieHeight,
        isLoading,
        regPageExposeAt,
        userID
    } from '../store/index';
        import { sendEventOnce } from '../utils/event';
    import { getH5creditCardUrl, JumpLink } from '../utils/jumpTo';
    import { pickPassportLoginFields } from '../utils/loginAuth';
    import { checkPhone, ensurePassportReady } from '../utils/passport';
    import {
        getMatchedRewardActivityId,
        saveRewardClaim
    } from '../utils/rewardClaimStorage';
    import { trackEvent } from '../utils/track';

    import Toast from './Toast.svelte';

    import webp from '@/actions/webp';
    import { getCookie } from '@/utils/cookie';
    import { debounce } from '@/utils/debounce';
    import { markPopEnd, markPopBegin, pageLeave } from '@/utils/fmpTrack';
    import { loginSuccessIframe, initializePreloadIframe } from '@/utils/preload';
        import { trackEventModuleSw } from '@/utils/techTrack';
    import url from '@/utils/url';

    export let themeType: ThemeTypeEnum = ThemeTypeEnum.WHITE;

    $: resolvedTheme =
        themeType === ThemeTypeEnum.NULL ? ThemeTypeEnum.WHITE : themeType;
    $: themeModifierClass = `ThemeTypeEnum--${resolvedTheme}`;

    let toastRef: Toast;
    let scrollElement: HTMLElement;

    let scrollAnimFrame: number | null = null;

    const cancelCookieScrollAnim = () => {
        if (scrollAnimFrame !== null) {
            cancelAnimationFrame(scrollAnimFrame);
            scrollAnimFrame = null;
        }
    };

    /** 相对当前位置匀速补滚，时长见 `COOKIE_SCROLL_DURATION_MS`（短于 Cookie 条动画） */
    const scrollByLinear = (deltaY: number) => {
        if (deltaY <= 0.5) return;

        cancelCookieScrollAnim();
        const startY = window.scrollY;
        const endY = startY + deltaY;
        const t0 = performance.now();

        const step = (now: number) => {
            const t = Math.min(
                1,
                (now - t0) / COOKIE_SCROLL_DURATION_MS
            );
            const y = startY + (endY - startY) * t;

            window.scrollTo(0, y);

            if (t < 1) {
                scrollAnimFrame = requestAnimationFrame(step);
            } else {
                scrollAnimFrame = null;
            }
        };

        scrollAnimFrame = requestAnimationFrame(step);
    };

    const showToast = (message: string) => {
        toastRef?.show(message);
    };

    const MAX_LENGTH = url.get('is_test') ? 11 : 10; // 输入框的最大长度
    let phoneDigits = '';
    let isInputError = false;
    let tipsMessage = '';
    let phoneInputEl: HTMLInputElement;
    let isInputFocused = false;
    let hasTypedAnyDigit = false;
    let isJustCleared = false;
    let passportReady = false;
    let passportBindAlive = true;
    let lastPhoneInputBlurReportedDigits = '';

    // 用于把"一次登录/注册尝试"里的 fail 埋点收敛为 1 次（全局维度，避免多组件实例/多监听叠加）
    let loginAttemptToken = 0;
    const LOGIN_ATTEMPT_TOKEN_KEY = '__mx_creditcard_msi_reward_login_attempt_token__';
    const LOGIN_FAIL_REPORTED_TOKEN_KEY = '__mx_creditcard_msi_reward_login_fail_reported_token__';

    const beginLoginAttempt = () => {
        loginAttemptToken = Date.now();
        (window as any)[LOGIN_ATTEMPT_TOKEN_KEY] = loginAttemptToken;
        (window as any)[LOGIN_FAIL_REPORTED_TOKEN_KEY] = 0;
    };

    // 输入框 blur 可能因抖动/重渲染触发多次：统一用防抖合并，最终只上报一次
    const reportPhoneInputBlurDebounced = debounce(() => {
        // 过滤"进页面自动 focus/blur"导致的无输入上报
        if (!hasTypedAnyDigit && phoneDigits.length === 0) return;

        // digits 未变化时不重复上报
        if (phoneDigits === lastPhoneInputBlurReportedDigits) return;
        lastPhoneInputBlurReportedDigits = phoneDigits;

        trackEvent(REG_PHONE_INPUT_SW, {
            is_phone_input_full: phoneDigits.length === MAX_LENGTH ? 1 : 0,
            is_error: isInputError ? 1 : 0
        });
    }, 800);

    const handleTipsMessage = (isError: boolean, message: string) => {
        isInputError = isError;
        tipsMessage = message;
    };

    const formatPhone = (digits: string) => {
        // 2-4-4 分组，和 MXLoginPage 保持一致
        return [
            digits.slice(0, 2),
            digits.slice(2, 6),
            digits.slice(6, MAX_LENGTH)
        ]
            .filter(Boolean)
            .join(' ');
    };

    const onPhoneInputFormat = (
        inputEl: HTMLInputElement,
        originalValue: string
    ) => {
        const originalCaret = inputEl?.selectionStart ?? originalValue.length;
        const digitsBeforeCaret = originalValue
            .slice(0, originalCaret)
            .replace(/\D/g, '').length;

        const digits = originalValue.replace(/\D/g, '').slice(0, MAX_LENGTH);
        const formatted = formatPhone(digits);

        phoneDigits = digits;

        inputEl.value = formatted;
        handleTipsMessage(false, '');

        // 恢复光标位置（基于"光标前有多少个数字"）
        let newCaret = 0;

        if (digitsBeforeCaret > 0) {
            let seenDigits = 0;

            for (let i = 0; i < formatted.length; i++) {
                if (/\d/.test(formatted[i])) {
                    seenDigits++;
                    if (seenDigits === digitsBeforeCaret) {
                        newCaret = i + 1;
                        break;
                    }
                }
            }

            if (newCaret === 0) newCaret = formatted.length;
        }

        inputEl.setSelectionRange(newCaret, newCaret);
    };

    const handleInput = (e: Event) => {
        const el = e.currentTarget as HTMLInputElement;

        onPhoneInputFormat(el, el.value);
        hasTypedAnyDigit = phoneDigits.length > 0;
        isJustCleared = false;
    };

    const focusPhoneInput = () => {
        if (isInputFocused) return;
        isInputFocused = true;
        phoneInputEl?.focus();
    };

    const handlePhoneInputClick = () => {
        // 仅用户点击输入框时上报；避免代码里调用 focus()（如校验失败）误上报
        trackEvent(PAGE_CLICK_CK, {
            button_name: 'phone_number_input',
            is_phone_input_full: phoneDigits.length === MAX_LENGTH ? 1 : 0
        });
    };

    const handleBlurInput = () => {
        // 避免异常抖动导致 blur 重复上报（比如组件重渲染/焦点来回切）
        if (!isInputFocused) return;

        isInputFocused = false;
        isJustCleared = false;

        // 用户点击输入框外部导致失焦时，校验手机号位数并展示报错
        if (phoneDigits.length > 0) {
            if (phoneDigits.length < MAX_LENGTH || isAllZeroPhone(phoneDigits)) {
                showInvalidPhoneError();
            } else {
                handleTipsMessage(false, '');
            }
        }

        reportPhoneInputBlurDebounced();
    };

    const handleEmptyInput = () => {
        if (!phoneInputEl) return;

        trackEvent(PAGE_CLICK_CK, {
            button_name: 'close',
            is_phone_input_full: phoneDigits.length === MAX_LENGTH ? 1 : 0
        });

        phoneDigits = '';
        phoneInputEl.value = '';
        isJustCleared = true;
        handleTipsMessage(false, '');
        isInputFocused = true;
        phoneInputEl?.focus();
    };

    const isAllZeroPhone = (digits: string) => {
        if (!digits) return false;

        return Number(digits) === 0;
    };

    const showInvalidPhoneError = () => {
        handleTipsMessage(
            true,
            'Por favor, ingresa tu número de celular completo'
        );
    };

    $: isPhoneValid = phoneDigits.length === MAX_LENGTH && !isAllZeroPhone(phoneDigits);
    $: shouldShowProgress = phoneDigits.length > 0;
    $: confirmBtnDisabled = (() => {
        if ($authChecking || (!passportReady && !$isLogin)) return true;
        if ($isLogin) return false;
        if (isPhoneValid) return false;
        if (phoneDigits.length === 0) {
            return !(isJustCleared || !hasTypedAnyDigit);
        }

        if (isInputFocused) return true;

        return true;
    })();

    /** 新链路：按钮 loading，领奖结束后跳转申卡 */
    const receiveRewardWithLoadingThenJump = async (
        authOverride?: ReturnType<typeof pickPassportLoginFields>
    ) => {
        $isLoading = true;
        isJumpOneLInk.set(false);
        pageLeave();
        try {
            const res: any = await receiveReward(authOverride);
            const rewardActivityId =
                res?.activityFullDTOList[0]?.activityDTO?.activityId ?? '';

            if (rewardActivityId) {
                $activityId = rewardActivityId;
            }

            trackEventModuleSw('receive_reward_success');
            saveRewardClaim({
                pageId: url.get('pageId'),
                uid: getCookie('uid') ?? get(userID) ?? '',
                activityId: rewardActivityId
            });
            isLogin.set(true);
            trackEvent(AWARD_PAGE_EXPOSE_SW, {
                result: 1
            });
        } catch (err: any) {
            pageLeave();
            const message = (err?.message ?? '').toString();
            const isTimeout = /timeout|time out/i.test(message);

            trackEvent(AWARD_PAGE_EXPOSE_SW, {
                result: 0,
                error_msg: err?.errmsg || message || 'receive_reward_failed',
                error_code: err?.errno || (isTimeout ? 'timeout' : '1005')
            });
            if (isTimeout) trackEventModuleSw('receive_reward_timeout');
            trackEventModuleSw('receive_reward_fail');
        }

        JumpLink(JUMP_TYPE_ENUM.TO_H5CREDITCARD);
    };

    const handleConfirmClick = async () => {
        if ($isLoading) return;
        trackEvent(PAGE_CLICK_CK, {
            button_name: 'next',
            is_phone_input_full: phoneDigits.length === MAX_LENGTH ? 1 : 0
        });

        // 已登录：已落盘则直达；否则后台领奖并立即跳转申卡
        if ($isLogin) {
            $isLoading = true;
            const claimedAid = getMatchedRewardActivityId();

            if (claimedAid) {
                $activityId = claimedAid;
                pageLeave();
                await new Promise((resolve) => setTimeout(resolve, 300));
                await JumpLink(JUMP_TYPE_ENUM.TO_H5CREDITCARD);
                await new Promise((resolve) => setTimeout(resolve, 2000));
                $isLoading = false;

                return;
            }

            await receiveRewardWithLoadingThenJump();
            $isLoading = false;

            return;
        }

        if (phoneDigits.length < MAX_LENGTH) {
            showInvalidPhoneError();
            focusPhoneInput();

            return;
        }

        if (isAllZeroPhone(phoneDigits)) {
            showInvalidPhoneError();
            focusPhoneInput();

            return;
        }

        beginLoginAttempt();
        try {
            $isLoading = true;
            handleTipsMessage(false, '');
            isJumpOneLInk.set(true);
            markPopBegin({ popup_id: POPUP_ID.PASSPORT_POPUP });
            initializePreloadIframe(getH5creditCardUrl(), false);
            checkPhone(phoneDigits)
                .then(async (res) => {
                    loginSuccessIframe(res);
                    $isLoading = true;
                    trackEventModuleSw('login_success');
                    trackEvent(REG_LOGIN_SUCCESS_SW);
                    sendEventOnce(5002);

                    // 新链路：先展示 Loading 页，领奖完成后跳转申卡
                    await receiveRewardWithLoadingThenJump(
                        pickPassportLoginFields(res)
                    );
                    await new Promise((resolve) => setTimeout(resolve, 300));
                    isJumpOneLInk.set(false);
                })
                .catch((err) => {
                    trackEventModuleSw('login_fail');
                    isJumpOneLInk.set(false);
                    $isLoading = false;
                    const errCode = err?.errno;
                    const errMsg = err?.error ?? 'login_failed';

                    debounce(() => {
                        trackEvent(REG_LOGIN_FAIL_SW, {
                            result: 0,
                            error_msg: errMsg,
                            error_code: errCode,
                            trace_id: err?.traceId
                        });
                    }, 500)();
                });
        } catch (e: any) {
            $isLoading = false;
            isJumpOneLInk.set(false);
            debounce(() => {
                trackEvent(REG_LOGIN_FAIL_SW, {
                    result: 0,
                    error_msg: 'passport_failed',
                    error_code: '10001',
                });
            }, 500)();
        }
    };

    const bindLoginRender = () => {
        window.login.on('render', (event: any) => {
            $isLoading = false;
            if (event?.to === 'code') {
                markPopEnd({ popup_id: POPUP_ID.PASSPORT_POPUP });
                trackEvent(PHONE_VERIFY_SW, {
                    result: 1,
                });
            }

        });
    };
    const bindLoginError = () => {
        window.login.on('error', (event: any) => {
            const errCode = event?.error?.errno;
            const errMsg = event?.error?.error;

            if (errCode === 40001) {
                $isLoading = false;
                isJumpOneLInk.set(false);
                trackEvent(PHONE_VERIFY_SW, {
                    result: 0,
                    error_msg: errMsg,
                    error_code: errCode,
                    trace_id: event?.error?.traceId
                });

                return;
            } else {
                trackEventModuleSw('login_fail');
                trackEvent(REG_LOGIN_FAIL_SW, {
                    result: 0,
                    error_msg: errMsg,
                    error_code: errCode,
                    trace_id: event?.error?.traceId
                });
            }
        });
    };
    const bindLoginEventsOnce = () => {
        const KEY = '__mx_creditcard_msi_reward_passport_login_events_bound__';

        if ((window as any)[KEY]) return;
        if (!window.login) return;
        (window as any)[KEY] = true;
        bindLoginRender();
        bindLoginError();
    };
    // 查看协议
    const handleCurpJumpLink = () => {
        pageLeave();
        trackEvent(PAGE_CLICK_CK, {
            button_name: 'view_agreement',
            is_phone_input_full: phoneDigits.length === MAX_LENGTH ? 1 : 0
        });
    };

    onMount(() => {
        passportBindAlive = true;
        void ensurePassportReady()
            .then(() => {
                if (!passportBindAlive) return;
                passportReady = true;
                bindLoginEventsOnce();
            })
            .catch((error) => {
                console.error('[ensurePassportReady]', error);
                showToast(
                    'No se pudo cargar el inicio de sesión. Inténtalo de nuevo.'
                );
            });

        // 初始化占位提示
        handleTipsMessage(false, '');
    });

    $: if ($cookieHeight === 0) {
        cancelCookieScrollAnim();
    }

    $: if ($cookieHeight > 0 && scrollElement) {
        const cookieBarHeight = $cookieHeight;

        requestAnimationFrame(() => {
            try {
                const elementRect = scrollElement.getBoundingClientRect();
                const windowHeight = window.innerHeight;
                const distanceFromBottom = windowHeight - elementRect.bottom;
                const gap = 20;

                if (distanceFromBottom < cookieBarHeight) {
                    scrollByLinear(
                        cookieBarHeight - distanceFromBottom + gap
                    );
                }
            } catch (e) {
                console.log(e);
            }
        });
    }

    onDestroy(() => {
        passportBindAlive = false;
        cancelCookieScrollAnim();
        // passport SDK 的事件解绑能力不明确，这里不做 off，避免误用导致异常
    });
</script>

<div class="phone-input-component {themeModifierClass}"
class:phone-input-component--white={themeType === ThemeTypeEnum.WHITE && $isLogin}
class:phone-input-component--black={themeType === ThemeTypeEnum.BLACK && $isLogin}
>
    {#if !$isLogin}
        <div
            role="none"
            class="phone-input-area"
            class:is-input-error={isInputError}
            on:click={focusPhoneInput}
        >
            <div class="country-flag">
                <img use:webp src={countryIcon} alt="" />
            </div>
            <div class="calling-code">+52</div>
            <input
                bind:this={phoneInputEl}
                class="phone-input"
                type="tel"
                placeholder="55 1234 5678"
                on:focus={focusPhoneInput}
                on:click|stopPropagation={handlePhoneInputClick}
                on:blur={handleBlurInput}
                on:input={handleInput}
            />
            {#if shouldShowProgress}
                <div
                    class="input-length-show"
                    class:is-input-complete={phoneDigits.length === MAX_LENGTH}
                >
                    <span>{phoneDigits.length}</span>/{MAX_LENGTH}
                </div>
            {/if}
            {#if phoneDigits.length}
                <div
                    class="close-icon"
                    role="none"
                    on:click|stopPropagation={handleEmptyInput}
                >
                    <img use:webp src={closePhoneIcon} alt="" />
                </div>
            {/if}
        </div>
        {#if tipsMessage}
            <div class="input-tips">
                <div class="tips-message" class:is-input-error={isInputError}>
                    {tipsMessage}
                </div>
            </div>
        {/if}
    {/if}
    <div
        class="confirm-btn"
        role="none"
        on:click={handleConfirmClick}
        class:disabled={confirmBtnDisabled}
    >
        <div class="confirm-btn-text">
            {#if $isLoading || ($authChecking && !$isLogin) || (!passportReady && !$isLogin)}
                <div class="loading-dots">
                    <span class="dot"></span>
                    <span class="dot"></span>
                    <span class="dot"></span>
                </div>
            {:else}
                Continuar
            {/if}
        </div>
    </div>
    {#if !$isLogin}
        <div class="policy">
            <a
                href={CREDIT_CARD_PROTOCOL}
                class="policy__full-link"
                rel="noopener"
                target="_blank"
                tabindex="0"
                on:click={handleCurpJumpLink}
            >
                <span
                    class="policy__text"
                    style:color={resolvedTheme === ThemeTypeEnum.WHITE
                        ? '#999999'
                        : '#ffffff'}
                    style:opacity={resolvedTheme === ThemeTypeEnum.WHITE
                        ? '1'
                        : '0.5'}
                >
                    Al continuar, aceptas los{' '}
                </span>
                <span class="policy__link-text">
                    Términos y Condiciones y el Aviso de Privacidad.
                </span>
            </a>
        </div>
        <div class="scroll-element" bind:this={scrollElement}></div>
    {/if}
</div>

<Toast bind:this={toastRef} />

<style lang="scss">
    .phone-input-component {
        padding: 40px 40px 60px 40px;

        .phone-input-area {
            margin-bottom: 28px;
            position: relative;
            box-sizing: border-box;
            min-height: 104px;
            height: 104px;
            display: flex;
            align-items: center;
            background: #f2f2f5;
            border-radius: 20px;
            padding: 0 20px 0 20px;
            .country-flag {
                width: 52px;
                height: 32px;
                flex-shrink: 0;
                img {
                    width: 100%;
                    height: 100%;
                    display: block;
                }
            }

            .calling-code {
                font-family: DiDiSans-Pro-Bold;
                font-size: 40px;
                font-weight: bold;
                line-height: 56px;
                letter-spacing: 0px;
                margin: 0 21px 0 11px;
                flex-shrink: 0;
                color: #0b0b0d;
            }

            .phone-input {
                width: 300px;
                height: 40px;
                margin-right: 140px;
                flex: 1;
                border: none;
                outline: none;
                background: none;
                font-family: DiDiSans-Pro-Bold;
                font-weight: bold;
                font-size: 40px;
                color: #0c0c0d;
                &::placeholder {
                    color: #bababf;
                }
            }

            &.is-input-error {
                border: 2px solid #f2183d;
                .phone-input {
                    color: #f2183d;
                }
            }

            .close-icon {
                width: 40px;
                height: 40px;
                position: absolute;
                right: 20px;
                top: 50%;
                transform: translateY(-50%);
                img {
                    width: 100%;
                    height: 100%;
                    display: block;
                }
            }

            .input-length-show {
                position: absolute;
                right: 80px; // keep it to the left of `close-icon`
                top: 50%;
                transform: translateY(-50%);
                font-family: DiDiSans-Pro-Normal;
                font-size: 28px;
                font-weight: normal;
                line-height: 36px;
                letter-spacing: 0px;
                color: #0b0b0d;
                span {
                    color: #000000;
                }
                &.is-input-complete {
                    span {
                        color: #000000;
                    }
                }
            }
        }

        .input-tips {
            display: flex;
            height: 36px;
            position: relative;
            margin-bottom: 22px;

            .tips-message {
                font-family: DiDiSans-Pro-Regular;
                font-size: 24px;
                font-weight: normal;
                line-height: 32px;
                letter-spacing: 0px;
                color: #0c0c0d;
                display: flex;
                margin-right: 0;
                &.is-input-error {
                    color: #f2183d;
                }
            }
        }

        .confirm-btn {
            position: relative;
            width: 100%;
            box-sizing: border-box;
            min-height: 104px;
            height: 104px;
            border-radius: 20px;
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 6;
            padding: 28px 32px;
            /* 白/黑主题：按钮统一黄色 */
            background: #ff5f14;

            .confirm-btn-text {
                display: flex;
                align-items: center;
                font-family: DiDiSans-Pro-ExtraBold;
                font-size: 40px;
                font-weight: 800;
                line-height: 64px;
                letter-spacing: 0px;
                color: #fff;
            }
            .loading-dots {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 0.1rem;

                .dot {
                    width: 0.16rem;
                    height: 0.16rem;
                    background: #ffffff;
                    border-radius: 50%;
                    opacity: 0.5;
                    transform: translateZ(0);

                    @for $i from 1 through 3 {
                        &:nth-child(#{$i}) {
                            animation: opacity-change 0.9s ease-in-out infinite;
                            animation-delay: #{($i - 1) * 0.3}s;
                        }
                    }
                }
            }
            @keyframes opacity-change {
                0%,
                100% {
                    opacity: 0.5;
                }
                50% {
                    opacity: 1;
                }
            }

            &.disabled {
                background: #f2f2f5;
                .confirm-btn-text {
                    color: #bababf;
                }
            }
        }

        .policy {
            font-family: DiDiSans-Pro-Normal;
            margin-top: 20px;
            font-size: 24px;
            line-height: 32px;
            letter-spacing: 0px;
            margin-left: 16px;
            color: #0b0b0d;

            a {
                text-decoration: none;
            }

            .policy__full-link {
                display: inline-block;
                padding: 8px 0;
            }

            .policy__text {
                color: #0b0b0d;
            }

            .policy__link-text {
                color: #101010;
                font-family: DiDiSans-Pro-Regular;
            }
        }

        &.ThemeTypeEnum--WHITE .policy .policy__link-text {
            font-family: DiDiSans-Pro-Regular;
        }

        &.ThemeTypeEnum--BLACK .policy {
            color: #ffffff;

            .policy__text {
                color: #ffffff;
            }

            .policy__link-text {
                color: #ffffff;
                font-family: DiDiSans-Pro-Regular;
            }
        }
    }
    .phone-input-component--white {
        padding-top: 60px;
    }
    .phone-input-component--black {
        padding-bottom: 0px;
    }
</style>
